import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { ArrowCounterClockwise, Brain, CalendarDots, CheckCircle, Flag, LockSimple, Plus, Sparkle, Trash } from '@phosphor-icons/react'
import { apiRequest, errorMessage, jsonBody } from '../api/client'
import type { ActivePlan, PlanRevisionDraft, PlanRevisionMutation, PlanSummary } from '../api/types'
import { formatPrescription, rolloutLabel } from '../app/format'
import { queryClient } from '../app/queryClient'
import { OwnerPlanV4PreparationPanel } from '../components/OwnerPlanV4PreparationPanel'
import { EmptyState, ErrorState, PageLoader } from '../components/States'
import { WeeklyReviewPanel } from '../components/WeeklyReviewPanel'

const dayOrder: Record<string, number> = { Monday: 1, Tuesday: 2, Wednesday: 3, Thursday: 4, Friday: 5, Saturday: 6, Sunday: 7 }
const dayLabels: Record<string, string> = { Monday: 'Pondělí', Tuesday: 'Úterý', Wednesday: 'Středa', Thursday: 'Čtvrtek', Friday: 'Pátek', Saturday: 'Sobota', Sunday: 'Neděle' }
const dayTypeLabels: Record<string, string> = { train: 'Trénink', legs: 'Nohy', deload: 'Deload', recovery: 'Regenerace' }
const revisionTitles: Record<PlanRevisionDraft['kind'], string> = {
  TaperPreparation: 'Uvolnit místo po splnění cíle',
  TemporaryAccessory: 'Ověřit dočasný doplňkový cvik',
  TransitionMix: 'Vyzkoušet těžší variantu v bloku A',
  UserStructuralEdit: 'Upravit strukturu plánu',
}
const revisionSources: Record<PlanRevisionDraft['source'], string> = {
  System: 'Systémový návrh',
  Engine: 'Návrh pravidel plánu',
  Ai: 'Návrh AI coache',
  User: 'Vlastní návrh',
}
const reasonLabels: Record<string, string> = {
  FIRST_USABLE_HARDER_BACK_LEVER_SET: 'První čistá série v těžší Back Lever variantě',
  ONE_TEST_SET_WITH_BACK_OFFS: 'Jeden testovací set, potom bezpečný back-off',
  NO_FULL_VARIANT_SWITCH: 'Bez okamžitého úplného přepnutí varianty',
  REPEATED_COMPARABLE_GERMAN_HANG_BOTTLENECK: 'Opakovaný srovnatelný limit v German Hangu',
  ONE_TEMPORARY_ACCESSORY_MAXIMUM: 'Nejvýše jeden dočasný doplňkový cvik',
  REPLACES_EXISTING_FLEXIBLE_WORK: 'Nahrazuje flexibilní práci, nepřidává objem',
  EXPLICIT_CONFIRMATION_REQUIRED: 'Změna vyžaduje tvoje potvrzení',
}
const dateFormatter = new Intl.DateTimeFormat('cs-CZ', { day: 'numeric', month: 'numeric', year: 'numeric' })

function readDraftDiff(draft: PlanRevisionDraft): Record<string, unknown> {
  try {
    const value: unknown = JSON.parse(draft.diffJson)
    return value !== null && typeof value === 'object' && !Array.isArray(value)
      ? value as Record<string, unknown>
      : {}
  } catch {
    return {}
  }
}

function revisionSummary(draft: PlanRevisionDraft): string {
  const diff = readDraftDiff(draft)
  if (draft.kind === 'TransitionMix') {
    return 'Přidá jeden kontrolovaný test těžší varianty a poté back-off v současné variantě. Plný přechod neprovede.'
  }
  if (draft.kind === 'TemporaryAccessory') {
    const exposures = typeof diff.trialExposureCount === 'number' ? diff.trialExposureCount : 2
    return `Dočasně nahradí jeden flexibilní cvik na nejvýše ${exposures} srovnatelné expozice. Celkový objem nezvýší.`
  }
  if (draft.kind === 'TaperPreparation') {
    const count = Array.isArray(diff.operations) ? diff.operations.length : 1
    return `Odebere ${count === 1 ? 'jeden přípravný cvik' : `${count} přípravné cviky`} jen z budoucího rozpisu. Historii zachová a místo nechá prázdné.`
  }
  return 'Změní strukturu aktivní instance plánu. Zdrojový plán ani dosavadní historie se nepřepíšou.'
}

function formatLocalDate(value: string): string {
  return dateFormatter.format(new Date(`${value}T12:00:00`))
}

function formatTimestampDate(value: string): string {
  return dateFormatter.format(new Date(value))
}

export function PlanPage() {
  const active = useQuery({ queryKey: ['active-plan'], queryFn: () => apiRequest<ActivePlan>('/plans/active') })
  const plans = useQuery({ queryKey: ['plans'], queryFn: () => apiRequest<PlanSummary[]>('/plans') })
  const [confirmedDrafts, setConfirmedDrafts] = useState<Record<string, boolean>>({})
  const [lastAppliedRevision, setLastAppliedRevision] = useState<PlanRevisionMutation | null>(null)
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['active-plan'] })
    await queryClient.invalidateQueries({ queryKey: ['plans'] })
    await queryClient.invalidateQueries({ queryKey: ['dashboard'] })
  }
  const activate = useMutation({
    mutationFn: (sourcePlanId: string) => apiRequest(`/plans/${sourcePlanId}/activate`, {
      method: 'POST',
      body: jsonBody({ controlMode: 'AiManaged', aiCoachEnabled: true }),
    }),
    onSuccess: refresh,
  })
  const archive = useMutation({
    mutationFn: (sourcePlanId: string) => apiRequest(`/plans/${sourcePlanId}/archive`, { method: 'POST' }),
    onSuccess: refresh,
  })
  const acceptRevision = useMutation({
    mutationFn: (draft: PlanRevisionDraft) => apiRequest<PlanRevisionMutation>(`/plans/revision-drafts/${draft.id}/accept`, {
      method: 'POST',
      body: jsonBody({ expectedStateVersion: draft.stateVersion, explicitUserConfirmation: true }),
    }),
    onSuccess: async (result) => {
      setLastAppliedRevision(result)
      setConfirmedDrafts((current) => ({ ...current, [result.draftId]: false }))
      await refresh()
    },
  })
  const rejectRevision = useMutation({
    mutationFn: (draft: PlanRevisionDraft) => apiRequest<PlanRevisionMutation>(`/plans/revision-drafts/${draft.id}/reject`, {
      method: 'POST',
      body: jsonBody({ expectedStateVersion: draft.stateVersion }),
    }),
    onSuccess: refresh,
  })
  const undoRevision = useMutation({
    mutationFn: (revision: PlanRevisionMutation) => apiRequest<PlanRevisionMutation>(`/plans/revision-drafts/${revision.draftId}/undo`, {
      method: 'POST',
      body: jsonBody({ expectedStateVersion: revision.stateVersion, explicitUserConfirmation: true }),
    }),
    onSuccess: async () => {
      setLastAppliedRevision(null)
      await refresh()
    },
  })

  if (active.isPending || plans.isPending) return <PageLoader label="Načítám plán…" />
  if (active.isError) return <ErrorState error={active.error} onRetry={() => active.refetch()} />
  if (plans.isError) return <ErrorState error={plans.error} onRetry={() => plans.refetch()} />

  const plan = active.data
  const revisionDrafts = plan.revisionDrafts ?? []
  const grouped = Object.values(
    plan.items.reduce<Record<string, { dayOfWeek: string; dayName: string; dayType: string; items: typeof plan.items }>>((days, item) => {
      const day = days[item.dayOfWeek] ??= { dayOfWeek: item.dayOfWeek, dayName: item.dayName, dayType: item.dayType, items: [] }
      day.items.push(item)
      return days
    }, {}),
  ).sort((a, b) => (dayOrder[a.dayOfWeek] ?? 99) - (dayOrder[b.dayOfWeek] ?? 99))

  return (
    <div className="page-stack plan-page">
      <div className="screen-kicker">
        <span>Můj plán</span>
        <span>Aktivní tréninkový plán</span>
      </div>
      <header className="plan-hero">
        <div><p className="eyebrow">Používaný plán</p><h1>{plan.name}</h1><p>{plan.items.length} cviků · {plan.goals.length} cílů</p></div>
        <div className="status-stack">
          <span className="status-pill"><Brain size={17} /> {plan.controlMode === 'AiManaged' ? 'AI řízený plán' : plan.controlMode}</span>
          <span className="status-pill status-pill--quiet">{rolloutLabel(plan.coachRolloutMode)}</span>
        </div>
      </header>


      <OwnerPlanV4PreparationPanel activePlan={plan} />

      <WeeklyReviewPanel planActivationId={plan.activationId} rolloutMode={plan.coachRolloutMode} />

      {lastAppliedRevision && (
        <section className="notice-band notice-band--success" aria-live="polite">
          <CheckCircle size={23} weight="fill" />
          <div>
            <strong>Změna je v aktivním plánu</strong>
            <p>Změna byla bezpečně zapsaná. Historie zůstala beze změny.</p>
          </div>
          <button
            className="button button--quiet"
            type="button"
            onClick={() => undoRevision.mutate(lastAppliedRevision)}
            disabled={undoRevision.isPending}
          >
            <ArrowCounterClockwise size={18} /> {undoRevision.isPending ? 'Vracím…' : 'Vrátit změnu'}
          </button>
        </section>
      )}
      {undoRevision.isError && <p className="inline-error">{errorMessage(undoRevision.error)}</p>}

      {revisionDrafts.length > 0 && (
        <section className="content-section revision-section" aria-labelledby="revision-heading">
          <div className="section-heading">
            <div>
              <p className="eyebrow">Čeká na tvoje rozhodnutí</p>
              <h2 id="revision-heading"><span aria-hidden="true">01 /</span> Navržené změny plánu</h2>
              <p>Nic se nepoužije samo. Každý návrh můžeš nejdřív posoudit a po přijetí hned vrátit.</p>
            </div>
            <span className="status-pill status-pill--warning">{revisionDrafts.length} {revisionDrafts.length === 1 ? 'návrh' : 'návrhy'}</span>
          </div>
          <div className="revision-list">
            {revisionDrafts.map((draft) => {
              const isAi = draft.source === 'Ai'
              const isBusy = acceptRevision.isPending || rejectRevision.isPending || undoRevision.isPending
              return (
                <article className="revision-card" key={draft.id}>
                  <div className={`revision-card__icon ${isAi ? 'revision-card__icon--ai' : ''}`}>
                    {isAi ? <Brain size={22} weight="duotone" /> : <Sparkle size={22} weight="duotone" />}
                  </div>
                  <div className="revision-card__body">
                    <div className="revision-card__head">
                      <div>
                        <span className={`revision-source ${isAi ? 'revision-source--ai' : ''}`}>{revisionSources[draft.source]}</span>
                        <h3>{revisionTitles[draft.kind]}</h3>
                      </div>
                      <span className="revision-number">pro revizi {draft.expectedRevisionNumber}</span>
                    </div>
                    <p className="revision-summary">{revisionSummary(draft)}</p>
                    <div className="revision-meta">
                      <span>Účinné od {formatLocalDate(draft.effectiveFromLocalDate)}</span>
                      <span>Kontrola {formatTimestampDate(draft.reviewAt)}</span>
                      <span>Platnost do {formatTimestampDate(draft.expiresAt)}</span>
                    </div>
                    <ul className="revision-reasons" aria-label="Důvody návrhu">
                      {draft.reasonCodes.map((reason) => <li key={reason}>{reasonLabels[reason] ?? reason}</li>)}
                    </ul>
                    <label className="revision-confirmation">
                      <input
                        type="checkbox"
                        checked={confirmedDrafts[draft.id] ?? false}
                        onChange={(event) => {
                          const checked = event.currentTarget.checked
                          setConfirmedDrafts((current) => ({ ...current, [draft.id]: checked }))
                        }}
                      />
                      <span>Rozumím dopadu a chci změnit aktivní instanci plánu.</span>
                    </label>
                    <div className="button-row revision-actions">
                      <button
                        className="button button--primary"
                        type="button"
                        aria-label={`Přijmout změnu: ${revisionTitles[draft.kind]}`}
                        onClick={() => acceptRevision.mutate(draft)}
                        disabled={!confirmedDrafts[draft.id] || isBusy}
                      >
                        {acceptRevision.isPending && acceptRevision.variables?.id === draft.id ? 'Ukládám…' : 'Přijmout změnu'}
                      </button>
                      <button
                        className="button button--quiet"
                        type="button"
                        aria-label={`Odmítnout změnu: ${revisionTitles[draft.kind]}`}
                        onClick={() => rejectRevision.mutate(draft)}
                        disabled={isBusy}
                      >
                        Odmítnout
                      </button>
                    </div>
                  </div>
                </article>
              )
            })}
          </div>
          {(acceptRevision.isError || rejectRevision.isError) && (
            <p className="inline-error">{errorMessage(acceptRevision.error ?? rejectRevision.error)}</p>
          )}
        </section>
      )}

      <section className="content-section">
        <div className="section-heading"><div><p className="eyebrow">Cílové kontrakty</p><h2><span aria-hidden="true">02 /</span> Kam plán směřuje</h2></div></div>
        <div className="goal-grid">
          {plan.goals.map((goal) => (
            <article className="goal-card" key={goal.id}>
              <Flag size={20} weight={goal.priority === 'Primary' ? 'fill' : 'regular'} />
              <div><strong>{goal.name}</strong><span>{goal.priority === 'Primary' ? 'Hlavní cíl' : 'Podpůrný cíl'}</span></div>
              <span className={`goal-status goal-status--${goal.status.toLowerCase()}`}>{goal.status === 'Active' ? 'Aktivní' : goal.status}</span>
            </article>
          ))}
        </div>
      </section>

      <section className="content-section plan-schedule">
        <div className="section-heading"><div><p className="eyebrow">Týdenní struktura</p><h2><span aria-hidden="true">03 /</span> Rozpis tréninků</h2></div></div>
        <div className="day-list">
          {grouped.map(({ dayOfWeek, dayName, dayType, items }) => (
            <article className="day-section" key={dayOfWeek}>
              <header><CalendarDots size={21} /><div><strong>{dayLabels[dayOfWeek] ?? dayOfWeek} · {dayName}</strong><span>{dayTypeLabels[dayType] ?? dayType}</span></div><b>{items.length} cviků</b></header>
              <div className="day-exercises">
                {items.sort((a, b) => a.sortOrder - b.sortOrder).map((item, index) => (
                  <div className="plan-exercise-row" key={item.id}>
                    <span className="exercise-index">{index + 1}</span>
                    <div>
                      <div className="exercise-meta">
                        {item.blockCode && <span className={`block-tag block-tag--${item.blockCode.toLowerCase()}`}>{item.blockCode}</span>}
                        {item.supersetCode && <span className="superset-tag">Superset {item.supersetCode}</span>}
                        {item.manualLock && <span><LockSimple size={13} /> ručně zamčeno</span>}
                      </div>
                      <strong>{item.exerciseName}</strong>
                      <small>{formatPrescription(item.axis, item.targets, item.externalLoadKilograms)}</small>
                    </div>
                    {item.deloadActive && <span className="status-pill status-pill--warning">Deload</span>}
                  </div>
                ))}
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="content-section">
        <div className="section-heading">
          <div><p className="eyebrow">Správa</p><h2><span aria-hidden="true">04 /</span> Moje plány</h2></div>
          <button className="button button--quiet" type="button" disabled title="Tvůrce plánu se zpřístupní po výběru cviků v knihovně"><Plus size={18} /> Nový plán</button>
        </div>
        {plans.data.length === 0 ? <EmptyState title="Žádné plány" text="Zatím tu není žádný zdrojový plán." /> : (
          <div className="plan-switcher">
            {plans.data.map((item) => (
              <article key={`${item.sourcePlanId}-${item.planInstanceId ?? 'source'}`}>
                <div className="plan-switcher__icon">{item.isActive ? <CheckCircle size={22} weight="fill" /> : <CalendarDots size={22} />}</div>
                <div><strong>{item.name}</strong><span>{item.itemCount} cviků · {item.goalCount} cílů · verze {item.version}</span></div>
                <div className="button-row">
                  {!item.isActive && item.itemCount > 0 && <button type="button" className="button button--quiet" onClick={() => activate.mutate(item.sourcePlanId)} disabled={activate.isPending}>Aktivovat</button>}
                  {!item.isActive && <button type="button" className="icon-button" title="Archivovat plán" onClick={() => archive.mutate(item.sourcePlanId)} disabled={archive.isPending}><Trash size={18} /></button>}
                </div>
              </article>
            ))}
          </div>
        )}
        {(activate.isError || archive.isError) && <p className="inline-error">{errorMessage(activate.error ?? archive.error)}</p>}
      </section>
    </div>
  )
}